import '../../../dmView/style/App.css';
import {useState, useEffect} from 'react';
import { getImageUrl, getLevelData, effectImgMap, conditionImgMap, sizeOptions, typeOptions, alignmentOptions, raceOptions, isProd, addSign, generateUniqueId, exportHomebrewTraits } from '../../constants';
import SkillGrid from '../SkillGrid';
import EditStatDropdown from './EditStatDropdown';
import EditStat from './EditStat';
import GridWrap from './GridWrap';
import ContentString from './ContentString';
import { useHomebrewProvider } from '../../../../../providers/HomebrewProvider';
import EditAvatar from './EditAvatar';
import { BoldifyReplace } from './BoldifyReplace';
import SpellCasting from './SpellCasting';
import LairActions from './LairActions';
import EditSpellCasting from './EditSpellCasting';
import EditLairActions from './EditLairActions';
import EditStatOptionsSelect from './EditStatOptionsSelect';
import { conditionsOptionsExport, damageTypeOptionsExport, sensesOptionsExport } from '../../constants'
import OptionButton from '../EncounterColumn/OptionButton';
import bracketImg from '../../pics/icons/brackets.png'
import SyntaxLegendPopup from './SyntaxLegendPopup';

function removeAtPath(obj, pathStr) {
    const path = pathStr.split('.').map(p => (p.match(/^\d+$/) ? Number(p) : p));
    if (path.length === 0) return obj;

    function isEmptyObjectOrList(obj) {
        if (typeof obj !== "object" || obj === null) return false;
        if (Array.isArray(obj)) return obj.length === 0;
        // Remove if 'items' or 'entries' is an empty array
        if ('items' in obj && Array.isArray(obj.items) && obj.items.length === 0) return true;
        if ('entries' in obj && Array.isArray(obj.entries) && obj.entries.length === 0) return true;
        // Remove objects with only type/name/source/style keys and no items/entries
        const keys = Object.keys(obj);
        const allowed = ['type', 'name', 'source', 'style'];
        return !!(keys.every(k => allowed.includes(k)));
    }

    function helper(current, idx) {
        const key = path[idx];
        if (idx === path.length - 1) {
            // Remove at this index/key
            if (Array.isArray(current)) {
                if (typeof key === "number" && key >= 0 && key < current.length) {
                    const newArr = [
                        ...current.slice(0, key),
                        ...current.slice(key + 1)
                    ].filter(item => !isEmptyObjectOrList(item));
                    return newArr.length === 0 ? undefined : newArr;
                }
                return current;
            } else if (typeof current === "object" && current !== null) {
                const { [key]: _, ...rest } = current;
                return Object.keys(rest).length === 0 ? undefined : rest;
            }
            return current;
        }
        if (Array.isArray(current)) {
            const newArr = current.map((item, i) =>
                i === key ? helper(item, idx + 1) : item
            ).filter(item => item !== undefined && !isEmptyObjectOrList(item));
            return newArr.length === 0 ? undefined : newArr;
        } else if (typeof current === "object" && current !== null) {
            const updated = {
                ...current,
                [key]: helper(current[key], idx + 1)
            };
            // If the updated child is undefined, remove the property
            if (updated[key] === undefined) {
                const { [key]: _, ...rest } = updated;
                return Object.keys(rest).length === 0 ? undefined : rest;
            }
            // Remove if this object is now "empty"
            if (isEmptyObjectOrList(updated)) return undefined;
            return updated;
        }
        return current;
    }

    const result = helper(obj, 0);
    return result === undefined ? {} : result;
}

function updateAtPath(obj, pathStr, newValue) {
    const path = pathStr.split('.').map(p => (p.match(/^\d+$/) ? Number(p) : p));
    if (path.length === 0) return obj;

    function helper(current, idx) {
        const key = path[idx];
        if (idx === path.length - 1) {
            // At the target property/array index
            if (Array.isArray(current)) {
                return current.map((item, i) => (i === key ? newValue : item));
            } else if (typeof current === "object" && current !== null) {
                return { ...current, [key]: newValue };
            }
            return current;
        }
        if (Array.isArray(current)) {
            return current.map((item, i) =>
                i === key ? helper(item, idx + 1) : item
            );
        } else if (typeof current === "object" && current !== null) {
            return {
                ...current,
                [key]: helper(current[key], idx + 1)
            };
        }
        return current;
    }

    return helper(obj, 0);
}

const changeName = window.location.search.includes("becca"); // Query string

function exists(value) {
    return value && value !== '0';
}

function formatSpeed(speed) {
    if(!speed) {
        speed = {'walk': 30}
    }
    const order = changeName ? ['walk'] : ['walk', 'climb', 'burrow', 'swim', 'fly', 'hover'];
    const entries = order
        .filter(key => exists(speed[key])) 
        .map(key => [key, speed[key]]);
    
    const result = entries
        .map(([key, value], i, arr) => `${capsFirstLetter(key)}${key !== 'hover' ? ` ${value}` : ''}${i < arr.length - 1 ? ', ' : ''}`)
        .join('');
    
    // If no speed greater then 0 exists
    if(result.length === 0)
        return 'None'

    return result;
}

function capsFirstLetter(word) {
    if (!word)
        return ''; // Handle empty or undefined input
    return word.charAt(0).toUpperCase() + word.slice(1);
}

const CreatureInfo = ({creature}) => {
    let string = ``

    if(creature.size && creature.size !== '--') {
        string += `${creature.size} -`
    }

    if(creature.creature_type && creature.creature_type !== '--') {
        string += ` ${creature.creature_type}`
    }
    
    if(creature.subtype && creature.subtype !== '--') {
        string += ` (${creature.subtype})`
    }

    if(creature.group && creature.group !== "null" && creature.group !== '--') {
        string += ` (${creature.group})`
    } 
    
    if(creature.creature_alignment && creature.creature_alignment !== '--') {
        string += ` (${creature.creature_alignment})`
    }

    if(creature?.alignment) {
        string += ` (${creature.alignment})`
    }

    return <p><i>{string}</i></p>
}

const colors = {
    0: "green",
    1: "orange",
    2: "brown",
}

const StatBlock = ({selectedIndex, indexOf, currentEncounter, setCurrentEncounter, closeStatBlock, handleUploadMonsterImage, socket}) => {
    
    // Creature can be null from SearchColumn but not from EncounterColumn, Statblock is not shown if from EncounterColumn
    const [creature, setCreature] = useState(currentEncounter?.creatures[selectedIndex])
    const [creatureReset, setCreatureReset] = useState(currentEncounter?.creatures[selectedIndex])
    const {addToHomebrewList} = useHomebrewProvider();
    const [isEditMode, setIsEditMode] = useState(currentEncounter?.encounterName === "dmbuddy_newhomebrew")
    const [legendOpen, setLegendOpen] = useState(false)
    const [showFullImage, setShowFullImage] = useState(false)

    useEffect(() => {
        if(creature?.avatarUrl === null) {
            const setCreatureImage = async () => {
                let avatarUrl = await getImageUrl(creature.name, creature.creature_type)
                setCreature({...creature, avatarUrl})
            }

            setCreatureImage()
            
        }
    }, [creature])

    // If selectedIndex changes a new creature was clicked
    useEffect(() => {
        let newCreature = selectedIndex !== null ? currentEncounter?.creatures[selectedIndex] : null
        setCreature(newCreature)

        //only change creatureReset if its actually since this useeffect trigger on cancel
        if(newCreature.creatureGuid !== creatureReset.creatureGuid)
            setCreatureReset(newCreature)
        
    // eslint-disable-next-line
    }, [currentEncounter?.creatures[selectedIndex], selectedIndex]);

    useEffect(() => {
        // Auto set edit mode to true if its a from scratch Homebrew
        setIsEditMode(currentEncounter?.encounterName === "dmbuddy_newhomebrew")
    // eslint-disable-next-line
    }, [currentEncounter?.creatures[selectedIndex]?.creatureGuid]);

    const handleSpellChange = (e, cKey, path = undefined, index = undefined, send = false) => {
        const { value } = e.target;

        // From EditStatBig add and remove buttons for SpellCasting ensure path is only "spellcasting"
        if(['add', 'remove', 'change'].includes(cKey)) {
            // , maybe ability later
            if(path.includes(".")) {
                handleUserSpellActions(cKey, path, index, value, send)
            } else {
                // cKey is add or remove, adding or removing from total spellcasting [], path is spellcasting
                handleUserArrayActions(cKey, path, index)
            }
        } else if(index !== undefined) {
            //name, headerEntries
            handleArrayChange(value, cKey, path, index, send)
        }

    }

    const handleUserSpellActions = (cKey, path, index, newValue = undefined, send = false) => {
        let pathItems = path.split(".")
        let pathKey = pathItems[0]
        let spellType = pathItems[1]
        let spellIndex = parseInt(pathItems.at(-1))
        let spellCastingList = creature.spellcasting

        if(cKey === 'add') {
            let newSpell = spellType > 0 ? '{@spell New Spell}' : '{@spell New Cantrip}'
            if(pathKey === 'spells') {
                let spellObjTemplate = {
                    [spellType]: {
                        slots: newValue,
                        spells: []
                    }
                }

                if(!spellCastingList[index].spells) {
                    spellCastingList[index].spells = spellObjTemplate
                } else if(!spellCastingList[index].spells[spellType]) {
                    spellCastingList[index].spells[spellType] = {...spellObjTemplate[spellType]}
                }
                spellCastingList[index].spells[spellType].spells.push(newSpell)
            } else if(pathKey === 'daily' || pathKey === "week" || pathKey === "lr" || pathKey === "sr") {

                const timeFrame = pathKey 

                let spellObjTemplate = {
                    [spellType]: []
                }

                if(!spellCastingList[index][timeFrame]) {
                    spellCastingList[index][timeFrame] = spellObjTemplate
                } else if(!spellCastingList[index][timeFrame][spellType]) {
                    spellCastingList[index][timeFrame][spellType] = []
                }

                spellCastingList[index][timeFrame][spellType].push(newSpell)
            } else if(pathKey === 'will') {

                if(!spellCastingList[index].will) {
                    spellCastingList[index].will = []
                }
                spellCastingList[index].will.push(newSpell)
            }
        } else if (cKey === 'remove') {
            if(pathKey === 'spells') {

                if(spellIndex !== undefined)
                    spellCastingList[index].spells[spellType].spells = spellCastingList[index].spells[spellType].spells.filter((_, i) => i !== spellIndex)
                else {
                    delete spellCastingList[index].spells[spellType]
                }

            } else if(pathKey === 'daily' || pathKey === "week" || pathKey === "lr" || pathKey === "sr") {
                const timeFrame = pathKey 

                if(spellIndex !== undefined)
                    spellCastingList[index][timeFrame][spellType] = spellCastingList[index][timeFrame][spellType].filter((_, i) => i !== spellIndex)
                else {
                    delete spellCastingList[index][timeFrame][spellType]
                }

            } else if(pathKey === 'will') {
                if(spellIndex !== undefined)
                    spellCastingList[index].will = spellCastingList[index].will.filter((_, i) => i !== spellIndex)
                else {
                    delete spellCastingList[index].will
                }
            }
        }  else if (cKey === 'change') {
            if(pathKey === 'spells') {
                spellCastingList[index].spells[spellType].spells[spellIndex] = newValue
            } else if(pathKey === 'daily' || pathKey === "week" || pathKey === "lr" || pathKey === "sr") {
                spellCastingList[index][pathKey][spellType][spellIndex] = newValue
            } else if(pathKey === 'will') {
                spellCastingList[index].will[spellIndex] = newValue
            }
        }
        
        if(send) {
            setCurrentEncounter((prev) => {
                const updatedCreatures = [...prev.creatures];
    
                updatedCreatures[selectedIndex] = {
                    ...updatedCreatures[selectedIndex],
                    spellcasting: [...spellCastingList],
                };
    
                return {
                    ...prev,
                    creatures: updatedCreatures,
                };
            });
            //socket does not exist in search column statblock
            socket?.emit("creatureSpellCastingChange", spellCastingList, currentEncounter.creatures[selectedIndex].creatureGuid)
        } else {
            setCreature((c) => ({
                ...c,
                spellcasting: [...spellCastingList]
            }));
        }
    };

    const handleLairChange = (newValue, action, path) => {
        if(path === 'legendary_actions_count') {
            handleValueChange(newValue, path, true);
            return;
        }

        let cKey = path.split('.')[0]

        setCurrentEncounter(prev => {
            const updatedCreatures = [...prev.creatures];
            const currentCreature = updatedCreatures[selectedIndex];
            let object = Array.isArray(currentCreature[cKey]) ? [...currentCreature[cKey]] : [];
            if (action === 'remove') {
                // Remove .entry or .entries from the end of the path to delete the whole object
                if (path?.endsWith(".entry") || path?.endsWith(".entries")) {
                    path = path.replace(/\.(entry|entries)$/, '');
                }

                // Remove at path using removeAtPath utility
                const updatedCreature = removeAtPath(currentCreature, path);
                if(updatedCreature[cKey])
                    object = [...updatedCreature[cKey]];
                else {
                    // If the object is empty because of deletion, set it to an empty array
                    object = [];
                }

            } else if (action === 'add') {

                object.push({
                    name: "None",
                    entries: ["--"]
                });

            } else if (action === 'desc') {

                const parts = path.split('.');
                const last1 = parts[parts.length - 1];
                if (last1 === 'entries') {
                    newValue = [newValue]
                }

                const updatedCreature = updateAtPath(currentCreature, path, newValue);
                object = [...updatedCreature[cKey]];
            } else if (action === 'name') {
                // replace entries or entry with name in path
                const parts = path.split('.');
                const last = parts[parts.length - 1];
                if (last === 'entry' || last === 'entries') {
                    parts[parts.length - 1] = 'name';
                    path = parts.join('.');
                }

                let updatedCreature = updateAtPath(currentCreature, path, newValue);

                let slotsNum = 0
                const match = newValue.match(/\((\d+)\/[^)]+\)/); // Match (X/any text) pattern
                if(match) {
                    slotsNum = parseInt(match[1]) * 10
                    parts[parts.length - 1] = 'rechargeCount';
                    path = parts.join('.');

                    updatedCreature = updateAtPath(updatedCreature, path, slotsNum);
                }

                object = [...updatedCreature[cKey]];
            } else if (action.includes("Recharge")) {
                const updatedCreature = updateAtPath(currentCreature, path+".rechargeCount", newValue);
                object = [...updatedCreature[cKey]];
            }

            updatedCreatures[selectedIndex] = {
                ...currentCreature,
                [cKey]: object
            };

            socket?.emit('legendaryGroupChange', updatedCreatures[selectedIndex], cKey);

            return {
                ...prev,
                creatures: updatedCreatures,
            };
        });
    };

    const handleChange = (e, cKey, category = undefined, index = undefined, send = false) => {
        // From EditStatBig add and remove buttonns
        const { value, checked, type } = e.target;
        if(['add', 'remove'].includes(cKey)) {
            handleUserArrayActions(cKey, category, index)
        } else if(index !== undefined) {
            //Array options, Actions, Bonus Actions
            handleArrayChange(value, cKey, category, index, send)
        } else if(category) {
            //Nested objects, speed
            handleNestedChange(type === 'checkbox' ? checked : value, cKey, category, send)
        } else {
            //Single change on the top level, name, size, type
            handleValueChange(value, cKey, send)
        }
    }
   
    
    const handleValueChange = (value, cKey, send) => {
        // also change current hp if max hp is changing and the creature is at full hp 
        let hpCurrent = {};

        if(cKey === 'hit_points' && parseInt(creature.hit_points) === parseInt(creature.hit_points_current)) {
            hpCurrent = {hit_points_current: value}
        }

        const updatedCreature = { ...creature, ...hpCurrent, [cKey]: value};
        setCreature({...updatedCreature});

        if (send) {
            setCurrentEncounter((prev) => ({
                ...prev,
                creatures: prev.creatures.map((oldCreature, i) =>
                    i === selectedIndex ? { ...updatedCreature } : oldCreature
                ),
            }));

            // This is a weird check for the search selected creatures that have their skills (i.e. STR or STR_Save) edited
            if(currentEncounter.encounterName === 'dmbuddy_selected' && !isEditMode) {
                handleAddToHomebrew(creature)
            }

            if(cKey === 'hit_points' && parseInt(creature.hit_points) === parseInt(creature.hit_points_current)) {
                socket?.emit('playerHpChange', {hit_points: value, hit_points_current: value}, creature.creatureGuid, "dm");
            } else {
                socket?.emit('statBlockEdit', currentEncounter.creatures[selectedIndex].creatureGuid, cKey, value);
            }
        }
    };

    const handleNestedChange = (value, cKey, category, send) => {
        if (send || cKey === 'hover') {
            if(value !== currentEncounter.creatures[selectedIndex]?.[category]?.[cKey]) {
                socket?.emit('statBlockEditNestedObject', currentEncounter.creatures[selectedIndex].creatureGuid, category, cKey, value);
                setCurrentEncounter((prev) => {
                    const updatedCreatures = [...prev.creatures];
                    updatedCreatures[selectedIndex] = {
                        ...updatedCreatures[selectedIndex],
                        [category]: {
                            ...updatedCreatures[selectedIndex][category],
                            [cKey]: value,
                        },
                    };
                    return {
                        ...prev,
                        creatures: updatedCreatures,
                    };
                });
            }
        } else {
            setCreature((prev) => ({
                ...prev,
                [category]: {
                    ...prev[category],
                    [cKey]: value
                }
            }));
        }
    };

    const handleArrayChange = (value, cKey, category, index, send = false) => {
        if (send) {
            
            if(value !== "None" && value !== "--" && value !== currentEncounter.creatures[selectedIndex]?.[category]?.[index]?.[cKey]) {

                const abilityKeys = ["special_abilities", "actions", "bonus_actions", "reactions"];
                let updateSlots = 0
                if(cKey === 'name' && abilityKeys.includes(category)) {
                    const match = value.match(/\((\d+)\/[^)]+\)/); // Match (X/any text) pattern
                    if(match) {
                        updateSlots = parseInt(match[1]) * 10
                    }
                }

                socket?.emit('statBlockEditArrayUpdate', value, category, index, cKey, currentEncounter.creatures[selectedIndex].creatureGuid);
                
                setCurrentEncounter((prev) => {
                    const updatedCreatures = [...prev.creatures];
                    const updatedCategoryArray = updatedCreatures[selectedIndex][category] !== null ? [...updatedCreatures[selectedIndex][category]] : [{name: '', desc: ''}];

                    updatedCategoryArray[index] = {
                        ...updatedCategoryArray[index],
                        [cKey]: value,
                    };

                    if(updateSlots) {
                        updatedCategoryArray[index].rechargeCount = updateSlots
                        socket?.emit('statBlockEditArrayUpdate', updateSlots, category, index, "rechargeCount", currentEncounter.creatures[selectedIndex].creatureGuid);
                    }

                    updatedCreatures[selectedIndex] = {
                        ...updatedCreatures[selectedIndex],
                        [category]: updatedCategoryArray,
                    };

                    return {
                        ...prev,
                        creatures: updatedCreatures,
                    };
                });
            }
                
        } else {
            setCreature(prev => {
                // this catches the array object being empty i.e. [{}] instead of [{name: '', desc: ''}]
                const updatedArray = prev[category] !== null ? [...prev[category]] : [{name: '', desc: ''}]; // Copy the whole actions array
                updatedArray[index] = {
                    ...updatedArray[index], // Unwrap the old one  
                    [cKey]: value, // Then update the specific field of that index
                };
                return {
                    ...prev,
                    [category]: updatedArray, // Set the updated actions array for the creature
                };
            });
        }
    };

    const handleUserArrayActions = (cKey, category, index) => {
        let updatedArray = []

        if(cKey === 'add') {
            let newObject = {name: 'None', desc: '--'}
            if(category === 'spellcasting') {
                newObject = {
					"name": "Spellcasting",
					"type": "spellcasting",
					"headerEntries": [
						`${creature.name} is a Xth-level spellcaster. Spellcasting ability is CHA|WIS|INT (spell save {@dc X}, {@hit X} to hit with spell attacks). They regain expended spell slots when they finish a short or long rest, and know the following spells:`
					],
                    "spells": {
                        "0": {
							"slots": 0,
							"spells": ['{@spell New Cantrip}']
						},
						"1": {
							"slots": 3,
							"spells": ['{@spell New Spell}']
						}
					},
					"displayAs": "trait"
				}
            }

            let actionsArray = currentEncounter.creatures[selectedIndex][category] || []
            updatedArray = [...actionsArray, newObject]
        } else if (cKey === 'remove') {
            updatedArray = [...currentEncounter.creatures[selectedIndex][category].filter((_, i) => i !== index)]
        }

        socket?.emit("statBlockEditArrayAction", category, index, currentEncounter.creatures[selectedIndex].creatureGuid, cKey)

        setCurrentEncounter((prev) => {
            const updatedCreatures = [...prev.creatures];

            updatedCreatures[selectedIndex] = {
                ...updatedCreatures[selectedIndex],
                [category]: updatedArray,
            };

            return {
                ...prev,
                creatures: updatedCreatures,
            };
        });
    };

    const toggleEditMode = () => {
        //reset creature on cancel when selected from search column
        if(currentEncounter.encounterName === 'dmbuddy_selected' && isEditMode === true) {
            setCurrentEncounter(prev => {
                prev.creatures[selectedIndex] = creatureReset
                return prev
            })
        }
        setIsEditMode(!isEditMode)
    }

    const handleAddToHomebrew = (creature, action="") => {
        let homebrewCreature = creature;
        //If new homebrew is clicked
        let isNewEntry = action === "new"
        if(isNewEntry) {
            homebrewCreature = {...creature, dmb_homebrew_guid: generateUniqueId()}
            setCreature(homebrewCreature)
            setCurrentEncounter(prev => {
                prev.creatures[selectedIndex] = homebrewCreature
                return prev
            })
        }
        // Give the homebrew version a different creatureGuid
        addToHomebrewList({...homebrewCreature})
        setIsEditMode(false)
    }

    return (
        <div className='infoContainer'>
            {isEditMode ? (
                <>
                    <div className='statblockEditTitleContainer'>
                        <div>
                            <h3 className="titleFontFamily">Edit Creature: <i>{creature.name}</i></h3>
                        </div>
                        <div style={{display: 'flex', alignItems: 'flex-end', }}>
                            <button className="statblockEdit" onClick={() => handleAddToHomebrew(creature, "new")}>
                                Save New Homebrew
                            </button>
                            {creature?.dmb_homebrew_guid &&
                                <button className="statblockEdit" onClick={() => handleAddToHomebrew(creature)}>
                                    Save Homebrew
                                </button>
                            }
                            <button className="statblockEdit" onClick={toggleEditMode}>
                                {currentEncounter.encounterGuid ? 'Save Creature' : 'Cancel'}
                            </button>

                            <OptionButton src={bracketImg} message={`Popup Syntax Legend`} onClickFunction={() => { setLegendOpen(p => !p) }} imgClassName={'removeMargin'}/>
                        </div>
                        {legendOpen && <SyntaxLegendPopup isOpen={legendOpen}/>}
                    </div>

                    <div style={{overflowX: 'hidden', overflowY: 'auto', border: '3px solid #822000', padding: '8px 6px 4px 8px', borderRadius: '0px 0px 8px 8px', boxShadow: 'rgb(67, 67, 67) 1px 1px 9px 1px inset'}}>
                        <div className='topInfo shadowBox'>
                            <div className='encounterCreatureLeftContainer' >
                                <EditAvatar handleUploadMonsterImage={handleUploadMonsterImage} quickEdit={true} creature={creature}/>

                                <GridWrap columns={3}>
                                    <EditStat label={`Name ${!isProd ? creature?.creatureGuid : ''}`} value={creature?.name || ''} cKey={'name'} handleChange={handleChange} />
                                    <EditStatDropdown label={`Race ${!isProd ? creature?.dmb_homebrew_guid : ''}`} options={raceOptions} value={creature.subtype} cKey={'subtype'} handleChange={handleChange}/> 
                                </GridWrap>
                            </div>

                            <hr className="editlineSeperator" />
                            {!changeName &&
                                <GridWrap columns={3}>
                                    <EditStatDropdown label={"Size"} options={sizeOptions} value={creature.size} cKey={'size'} handleChange={handleChange}/>
                                    <EditStatDropdown label={"Type"} options={typeOptions} value={creature.creature_type} cKey={'creature_type'} handleChange={handleChange}/>
                                    <EditStatDropdown label={"Alignment"} options={alignmentOptions} value={creature.creature_alignment} cKey={'creature_alignment'} handleChange={handleChange}/>
                                </GridWrap>
                            }
                            <hr className="editlineSeperator" />
                            <GridWrap>
                                <EditStat label={"Max Hp"} value={creature.hit_points} cKey={'hit_points'} handleChange={handleChange} type='number'/>
                                <EditStat label={changeName ? "Max MP" : "AC"} value={creature.armor_class} cKey={'armor_class'} handleChange={handleChange} type='number'/>
                                {!changeName && <EditStat label={"Init Bonus"} value={addSign(creature.dexterity_save)} cKey={'dexterity_save'} handleChange={handleChange} type='number'/>}
                            </GridWrap>
                            <hr/>
                            <GridWrap columns={6} scroll={'auto'}>
                                <EditStat label={changeName ? "Speed" : "Walk"} value={creature.speed.walk || 0} cKey={'walk'} category={'speed'} handleChange={handleChange} type='number' />
                                {!changeName &&
                                    <>
                                        <EditStat label={"Climb"} value={creature.speed.climb || 0} cKey={'climb'} category={'speed'} handleChange={handleChange} type='number' />
                                        <EditStat label={"Burrow"} value={creature.speed.burrow || 0} cKey={'burrow'} category={'speed'} handleChange={handleChange} type='number' />
                                        <EditStat label={"Swim"} value={creature.speed.swim || 0} cKey={'swim'} category={'speed'} handleChange={handleChange} type='number' />
                                        <EditStat label={"Fly"} value={creature.speed.fly || 0} cKey={'fly'} category={'speed'} handleChange={handleChange} type='number' />
                                        <EditStat label={"Hover"} value={creature.speed.hover || false} cKey={'hover'} category={'speed'} handleChange={handleChange} type='checkbox' />
                                    </>
                                }
                            </GridWrap>
                            <hr className="editlineSeperator" />
                            <SkillGrid creature={creature} edit={true} handleChange={handleChange}/>
                        </div>
                        <GridWrap columns={2} paddingTop={15} scroll=''>
                            <EditStatOptionsSelect label={'Vulnerabilites'} creatureValues={creature.damage_vulnerabilities} options={damageTypeOptionsExport} cKey={'damage_vulnerabilities'} handleChange={handleChange}/>
                            <EditStatOptionsSelect label={'Senses'} creatureValues={creature.senses} options={sensesOptionsExport} cKey={'senses'} handleChange={handleChange}/>
                            <EditStatOptionsSelect label={'Immunities'} creatureValues={creature.damage_immunities} options={damageTypeOptionsExport} cKey={'damage_immunities'} handleChange={handleChange}/>
                            <EditStatOptionsSelect label={'Condition Immunities'} creatureValues={creature.condition_immunities} options={conditionsOptionsExport} cKey={'condition_immunities'} handleChange={handleChange}/>
                            <EditStatOptionsSelect label={'Resistances'} creatureValues={creature.damage_resistances} options={damageTypeOptionsExport} cKey={'damage_resistances'} handleChange={handleChange}/>
                        </GridWrap>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Traits"} content={creature?.special_abilities} category={'special_abilities'} options={exportHomebrewTraits} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditSpellCasting label={"Spell Casting"} spellcasting={creature?.spellcasting} category={'spellcasting'} handleChange={handleSpellChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Actions"} content={creature?.actions} category={'actions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Bonus Actions"} content={creature?.bonus_actions} category={'bonus_actions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Reactions"} content={creature?.reactions} category={'reactions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={'Inventory'} content={creature?.inventory} category={'inventory'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditStatDropdown label={"Legendary Actions Count"} options={[0,1,2,3,4,5,6,7,8,9,10]} value={Math.floor(creature?.legendary_actions_count/10) || 0} cKey={'legendary_actions_count'} handleChange={handleChange}/>
                            <EditLairActions label={"Legendary Actions"} content={creature?.legendary_actions} category={'legendary_actions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Mythic Actions"} content={creature?.mythic_actions} category={'mythic_actions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Lair Actions"} content={creature?.lair_actions} category={'lair_actions'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                            <EditLairActions label={"Regional Effects"} content={creature?.regional_effects} category={'regional_effects'} handleChange={handleLairChange}/>
                        <hr className="editlineSeperator" />
                        <GridWrap columns={2}>
                            <EditStat label={"Languages"} value={creature.languages} cKey={'languages'} handleChange={handleChange} />
                            {/* <EditStat label={"Environments"} value={creature.environments} cKey={'environments'} handleChange={handleChange} /> */}
                            <div style={{display: 'flex'}}>
                                <EditStat label={"CR"} value={creature.challenge_rating} cKey={'challenge_rating'} handleChange={handleChange} type='number'/>
                            </div>
                        </GridWrap>
                        <hr className="lineSeperator" />
                    </div>
                </>
            ) : (
                <>  
                    <div className='statblockOptionsFlex'>
                        <div>
                            <button className="statblockEditInfo" onClick={() => setShowFullImage(!showFullImage)}>i</button>
                        </div>
                            <div className='statblockOptionsRight'>
                            {!creature.dnd_b_player_id && (
                                <button className="statblockEdit" onClick={toggleEditMode}>
                                    {(currentEncounter.encounterGuid || creature?.dmb_homebrew_guid)
                                        ? "Edit"
                                        : "Use as Homebrew Template"
                                    }
                                </button>
                            )}

                                <button className='statblockX' onClick={closeStatBlock}><span style={{fontSize: '11px'}}>❌</span></button>
                            </div>
                        
                    </div>

                    <div className='topInfo shadowBox'>
                        
                        <img className={showFullImage ? "clearImage" : "behindImage"} src={creature.avatarUrl} alt={"Creature Img"} />
                        {!showFullImage && 
                            <>
                                <h1 className='creatureName titleFontFamily'>{creature?.name}&nbsp;</h1>
                                
                                <div className='creatureType'>
                                    <hr className="lineSeperator" />
                                    <CreatureInfo creature={creature}/>
                                    {(currentEncounter.encounterName !== 'dmbuddy_selected' && currentEncounter.encounterName !== 'dmbuddy_newhomebrew' && indexOf !== undefined) && 
                                        <div className='selectedIndicater siStatBlock' style={{backgroundColor: colors[indexOf]}}> {indexOf+1}</div>
                                    }    
                                </div>
                                {creature.effectObjs.length > 0 &&
                                    <div style={{backgroundColor: "black", width: 'fit-content', height: '42px', borderRadius: 5}}>
                                        {creature.effectObjs.map((effect) => {
                                            const effectName = effect.effectName;
                                            return (
                                                <img key={effectName} alt='effect' className='effect growImage' src={effectImgMap[effectName] || conditionImgMap[effectName]}/>
                                            )
                                        })}
                                    </div>
                                }
                                <div className='stickyStatGrid textShadow' >
                                    <p className="stickyStatItem">
                                        <strong className='titleColor'>{changeName ? 'Max MP' : 'AC'}&nbsp;</strong>
                                        <BoldifyReplace desc={creature.armor_class} />
                                        {creature.armor_desc && creature.armor_desc !== "()" && creature.armor_desc !== "null" && creature.armor_desc !== "(null)" && 
                                            <>&nbsp;<BoldifyReplace desc={creature.armor_desc} /></>
                                        } 
                                    </p>
                                    <p className="stickyStatItem">
                                        <strong className='titleColor'>Initiative</strong>&nbsp;{addSign(Math.floor((creature.dexterity-10)/2))} 
                                        <span className='extraInfo'>&nbsp;({parseInt(creature.dexterity_save)+10 || 10})</span>
                                    </p>
                                    <p className="stickyStatItem stickyStatExtraWide">
                                        <strong className='titleColor'>HP&nbsp;</strong>{creature.hit_points_current}/{creature.hit_points}
                                        {creature.hit_points_temp !== 0 && (
                                            <span className='tempHp'>&nbsp;(+{creature.hit_points_temp}) </span>
                                        )}
                                        {creature.hit_points_formula && creature.hit_points === creature.hit_points_default && (
                                            <span className='extraInfo'>&nbsp;({creature?.hit_points_formula})</span>
                                        )}
                                    </p>
                                    {creature.hit_points_special && 
                                    <>
                                        {/* This is for spacing */}
                                        <p className="stickyStatItem"/>
                                        <p className="stickyStatItem stickyStatExtraWide">
                                            <strong className='titleColor'><i>HP Special&nbsp;</i></strong>
                                            <span className='extraInfo'>{creature.hit_points_special}</span>
                                        </p>
                                    </>
                                        
                                    }
                                    {/* This is for spacing */}
                                    <p className="stickyStatItem"/>

                                    <p className="stickyStatItem stickyStatExtraWide">
                                        <strong className='titleColor'>Speed</strong>&nbsp;
                                        {formatSpeed(creature.speed)}
                                    </p>
                                    <p className="stickyStatItem"></p>
                                </div>
                                <SkillGrid creature={creature} handleChange={handleChange}/>
                            </>
                        }

                    </div>
                        
                    <div className="statBlockScroll">
                        <ContentString label={'Skills'} contentString={creature.skills} />
                        <ContentString label={'Vulnerabilities'} contentString={creature.damage_vulnerabilities} />
                        <ContentString label={'Resistances'} contentString={creature.damage_resistances} />
                        <ContentString label={'Immunities'} contentString={creature.damage_immunities} />
                        <ContentString label={'Condition Immunities'} contentString={creature.condition_immunities?.split(', ').filter(str => str.trim() !== "").map((c) => {return `{@condition ${c}}`}).join(', ')} />
                        <ContentString label={'Senses'} contentString={creature.senses} />
                        <ContentString label={'Languages'} contentString={creature.languages} />
                        <ContentString label={'CR'} contentString={creature.challenge_rating} italics={`${parseInt(creature.challenge_rating) > 30 ? '' : `(${getLevelData(creature.challenge_rating)} XP)`}`}/>
                    
                        <LairActions content={creature.special_abilities} label={'TRAITS'} cKey={'special_abilities'} handleSlotRecharge={handleLairChange}/>
                        <SpellCasting creature={creature}/>
                        <LairActions content={creature.actions} label={'ACTIONS'} cKey={'actions'} handleSlotRecharge={handleLairChange}/>
                        <LairActions content={creature.bonus_actions} label={'BONUS ACTIONS'} cKey={'bonus_actions'} handleSlotRecharge={handleLairChange}/>
                        <LairActions content={creature.reactions} label={'REACTIONS'} cKey={'reactions'} handleSlotRecharge={handleLairChange}/>
                        <LairActions content={creature.inventory} label={'INVENTORY'} cKey={'inventory'} />
                        <LairActions content={creature.legendary_actions} label={'LEGENDARY ACTIONS'} cKey={'legendary_actions_count'} labelDesc={creature.legendary_desc} actions_count={creature.legendary_actions_count} handleSlotRecharge={handleLairChange} />
                        <LairActions content={creature.mythic_actions} label={'MYTHIC ACTIONS'} cKey={'mythic_actions'} labelDesc={creature.mythic_desc} />
                        <LairActions content={creature.lair_actions} label={'LAIR ACTIONS'} cKey={'lair_actions'} handleSlotRecharge={handleLairChange}/>
                        <LairActions content={creature.regional_effects} label={'REGIONAL EFFECTS'} cKey={'regional_effects'} handleSlotRecharge={handleLairChange}/>

                        {/* {creature.environments && (
                            <div className='extraInfo'>
                                <hr className="lineSeperator" />
                                <strong>Environments: </strong>
                                <span> {creature.environments}</span>
                            </div>
                        )} */}
                        {/* <div className='endShadowContainer'>
                            <div className='endShadow'/>
                        </div> */}
                        <hr className="lineSeperator lineSeperatorLast" />
                    </div>
                    {creature.sourceShort && 
                        <div>
                            <hr className="lineSeperator" />
                            <strong className='source'>Source: {creature.sourceShort}, page {creature.page}</strong>
                        </div>
                    }
                    <hr className="lineSeperator lineSeperatorEnd" />
                </>
            )}
        </div>
    );
}

export default StatBlock;
